import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Search, Package, User, Briefcase, Inbox, Loader2 } from "lucide-react";
import axios from "axios";
import api from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Entity registry ----------
// Each entity: how to fetch, how to filter, how to render, where to navigate.
const ENTITIES = [
  {
    key: "leads",
    label: "Leads",
    icon: Inbox,
    url: (role) => `${API}/leads?requester_role=${role}&limit=200`,
    idField: "lead_id",
    primary: (it) => it.name || "(unnamed lead)",
    secondary: (it) =>
      [it.phone, it.company_name].filter(Boolean).join(" · ") || "—",
    badge: (it) => it.lead_category || it.lead_score || null,
    searchFields: ["name", "phone", "company_name", "email", "city"],
    routeTo: () => `/leads`,
  },
  {
    key: "products",
    label: "Products",
    icon: Package,
    url: () => `${API}/products?limit=200`,
    idField: "product_id",
    primary: (it) => it.name || "(unnamed product)",
    secondary: (it) =>
      [it.brand_name, it.model_number || it.category].filter(Boolean).join(" · ") || "—",
    badge: (it) => it.category || null,
    searchFields: ["name", "brand_name", "model_number", "category", "description"],
    routeTo: (it) => `/products/${it.product_id}`,
  },
  {
    key: "contacts",
    label: "Contacts",
    icon: User,
    url: () => `${API}/crm/contacts`,
    idField: "contact_id",
    primary: (it) => it.name || "(unnamed contact)",
    secondary: (it) =>
      [it.company, it.email].filter(Boolean).join(" · ") || "—",
    badge: (it) => it.position || null,
    searchFields: ["name", "company", "email", "phone", "position"],
    routeTo: (it) => `/crm/contacts/${it.contact_id}/360`,
  },
  {
    key: "deals",
    label: "Deals",
    icon: Briefcase,
    url: () => `${API}/crm/deals`,
    idField: "deal_id",
    primary: (it) => it.title || "(unnamed deal)",
    secondary: (it) => {
      const parts = [];
      if (it.contact_name) parts.push(it.contact_name);
      if (it.value != null) parts.push(`₹${Number(it.value).toLocaleString("en-IN")}`);
      return parts.join(" · ") || "—";
    },
    badge: (it) => it.stage || null,
    searchFields: ["title", "contact_name", "stage", "notes"],
    routeTo: () => `/crm/deals`,
  },
];

// ---------- Match highlighter ----------
function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${safe})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={i}
            className="bg-primary/15 text-primary font-medium rounded px-0.5"
            data-testid="global-search-match-highlight"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// ---------- Main component ----------
export default function GlobalSearchPalette({ user }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [warming, setWarming] = useState(false);
  const [data, setData] = useState({ leads: [], products: [], contacts: [], deals: [] });
  const [warmedFor, setWarmedFor] = useState(null); // userId we warmed for
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // ---- Cmd+K / Ctrl+K opens ----
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const combo = (isMac ? e.metaKey : e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (combo) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [user]);

  // ---- Warm-up on first open per session ----
  useEffect(() => {
    if (!open || !user) return;
    if (warmedFor === user.user_id) return;
    let cancelled = false;
    (async () => {
      setWarming(true);
      const role = user.role || "";
      const results = await Promise.allSettled(
        ENTITIES.map((e) =>
          api.get(e.url(role)).then((r) => ({ key: e.key, items: Array.isArray(r.data) ? r.data : [] }))
        )
      );
      if (cancelled) return;
      const next = { leads: [], products: [], contacts: [], deals: [] };
      for (const r of results) {
        if (r.status === "fulfilled") next[r.value.key] = r.value.items;
        // 403 / network failures silently skip — RBAC-respecting
      }
      setData(next);
      setWarmedFor(user.user_id);
      setWarming(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user, warmedFor]);

  // ---- Filter + flatten ----
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ENTITIES.map((e) => {
      const items = data[e.key] || [];
      // hide group entirely if warm-up returned nothing (likely RBAC-gated)
      if (items.length === 0) return { ...e, items: [], hidden: true };
      const matched = q
        ? items.filter((it) =>
            e.searchFields.some((f) => {
              const v = it[f];
              return v != null && String(v).toLowerCase().includes(q);
            })
          )
        : items.slice(0, 5); // when empty query, show first 5 per group as "browse"
      return { ...e, items: matched.slice(0, 8), hidden: false };
    });
  }, [data, query]);

  const flatList = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      if (g.hidden) return;
      g.items.forEach((it) => out.push({ group: g, item: it }));
    });
    return out;
  }, [groups]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIdx(0);
  }, [query, data]);

  // ---- Keyboard nav inside palette ----
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, flatList.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        const sel = flatList[activeIdx];
        if (sel) {
          const path = sel.group.routeTo(sel.item);
          setOpen(false);
          setQuery("");
          navigate(path);
        }
      }
    },
    [flatList, activeIdx, navigate]
  );

  // Scroll active into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-row-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!user) return null;

  const totalShown = flatList.length;
  const showEmpty = !warming && totalShown === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="overflow-hidden p-0 max-w-2xl gap-0"
        data-testid="global-search-palette"
      >
        <DialogTitle className="sr-only">Global Search</DialogTitle>
        {/* Search input */}
        <div className="flex items-center border-b px-4 py-3" data-testid="global-search-input-wrapper">
          <Search className="mr-3 h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search leads, products, contacts, deals…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid="global-search-input"
          />
          <kbd className="ml-2 hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/* Body */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto">
          {warming && (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground" data-testid="global-search-loading">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          )}

          {!warming && !query && totalShown === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="global-search-empty-hint">
              Type to search…
            </div>
          )}

          {!warming && query && showEmpty && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="global-search-no-results">
              No results for <span className="font-medium text-foreground">"{query}"</span>
            </div>
          )}

          {!warming &&
            groups.map((g) => {
              if (g.hidden || g.items.length === 0) return null;
              const Icon = g.icon;
              return (
                <div key={g.key} className="px-2 py-2" data-testid={`global-search-group-${g.key}`}>
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" />
                    <span>{g.label}</span>
                    <span className="text-muted-foreground/70 normal-case tracking-normal">
                      ({g.items.length})
                    </span>
                  </div>
                  {g.items.map((it) => {
                    const idx = flatList.findIndex(
                      (f) => f.group.key === g.key && f.item[g.idField] === it[g.idField]
                    );
                    const isActive = idx === activeIdx;
                    return (
                      <button
                        key={it[g.idField]}
                        data-row-idx={idx}
                        data-testid={`global-search-result-${g.key}-${it[g.idField]}`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => {
                          setOpen(false);
                          setQuery("");
                          navigate(g.routeTo(it));
                        }}
                        className={`w-full text-left flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                          isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            <Highlight text={g.primary(it)} query={query} />
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            <Highlight text={g.secondary(it)} query={query} />
                          </div>
                        </div>
                        {g.badge(it) && (
                          <span className="ml-2 rounded-full border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {g.badge(it)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
        </div>

        {/* Footer hints */}
        <div
          className="flex items-center justify-between border-t px-4 py-2 text-[11px] text-muted-foreground"
          data-testid="global-search-footer"
        >
          <div className="flex items-center gap-3">
            <span><kbd className="rounded border bg-muted px-1 font-mono">↑</kbd> <kbd className="rounded border bg-muted px-1 font-mono">↓</kbd> navigate</span>
            <span><kbd className="rounded border bg-muted px-1 font-mono">↵</kbd> open</span>
            <span><kbd className="rounded border bg-muted px-1 font-mono">esc</kbd> close</span>
          </div>
          <span className="hidden sm:inline">Global Search</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
